import { TestBuild } from "../types/testBuild";
import { getFileExtension } from "./appVersions";

export interface TestBuildGroup {
  key: string; // `${variantId}__${versionName}__${versionCode}`
  variantId: string | number;
  versionName: string;
  versionCode: string;
  /** Highest iteration in the stack — what a QE should usually install. */
  latest: TestBuild;
  /** All iterations, DESCENDING so the newest reads first. */
  iterations: TestBuild[];
}

/**
 * Stable key for a stack of iterations. Deliberately excludes the display
 * name: the server's identity for a test build is variant + version, and the
 * name is free text a dev may reword between iterations of the same build.
 * Grouping on it would split one stack into several.
 */
export function testBuildKey(
  build: Pick<TestBuild, "variantId" | "versionName" | "versionCode">
): string {
  return `${build.variantId ?? ""}__${build.versionName ?? ""}__${build.versionCode ?? ""}`;
}

const compareByCreatedAt = (a: TestBuild, b: TestBuild): number => {
  const at = a.created_at ? Date.parse(a.created_at) : NaN;
  const bt = b.created_at ? Date.parse(b.created_at) : NaN;
  if (!Number.isNaN(at) && !Number.isNaN(bt) && at !== bt) {
    return at - bt;
  }
  return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
};

/**
 * Turn the flat list into per-version stacks. Without this, five uploads of
 * 1.4.0 (210) look like five unrelated rows and a QE installs the wrong one.
 *
 * The API already returns newest-first and already filters out expired builds,
 * so nothing here re-filters; it only regroups.
 */
export function groupTestBuilds(builds: TestBuild[]): TestBuildGroup[] {
  const stacks = new Map<string, TestBuild[]>();

  for (const build of builds) {
    const key = testBuildKey(build);
    const existing = stacks.get(key);
    if (existing) existing.push(build);
    else stacks.set(key, [build]);
  }

  const result: TestBuildGroup[] = [];
  for (const [key, iterations] of stacks) {
    // Descending by iteration; created_at breaks ties for any record whose
    // iteration the server did not set.
    iterations.sort((a, b) => {
      const diff = (b.iteration ?? 0) - (a.iteration ?? 0);
      return diff !== 0 ? diff : compareByCreatedAt(b, a);
    });
    const latest = iterations[0];
    result.push({
      key,
      variantId: latest.variantId,
      versionName: latest.versionName,
      versionCode: latest.versionCode,
      latest,
      iterations,
    });
  }

  result.sort((a, b) => compareByCreatedAt(b.latest, a.latest));
  return result;
}

/** Under this, the remaining time gets a warning treatment. */
export const EXPIRING_SOON_SECONDS = 24 * 60 * 60;

export function isExpiringSoon(seconds: number): boolean {
  return Number.isFinite(seconds) && seconds < EXPIRING_SOON_SECONDS;
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * Human expiry copy from the server-computed `expires_in_seconds`. Rendered
 * once per fetch — the list does not tick client-side, it refreshes after any
 * upload, delete, or expired download.
 */
export function formatExpiry(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "Expiring now";

  const days = Math.floor(seconds / 86400);
  if (days >= 1) return `Expires in ${plural(days, "day")}`;

  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) return `Expires in ${plural(hours, "hour")}`;

  const minutes = Math.floor(seconds / 60);
  if (minutes >= 1) return `Expires in ${plural(minutes, "minute")}`;

  return "Expiring now";
}

/** A test build lives for 7 days from upload, record and file. */
export const TEST_BUILD_LIFETIME_SECONDS = 7 * 24 * 60 * 60;

/** Under this (and over EXPIRING_SOON_SECONDS) the time left reads as a warning. */
export const EXPIRY_WARNING_SECONDS = 3 * 24 * 60 * 60;

export type ExpiryTone = "error" | "warning" | "success";

/** Colour tone for the time-left bar and chip. Never the only signal: the label always shows too. */
export function expiryTone(seconds: number): ExpiryTone {
  if (!Number.isFinite(seconds) || seconds < EXPIRING_SOON_SECONDS) return "error";
  if (seconds < EXPIRY_WARNING_SECONDS) return "warning";
  return "success";
}

/** Share of the 7-day lifetime still left, clamped to 0–1, for the progress bar. */
export function expiryFraction(seconds: number): number {
  if (!Number.isFinite(seconds)) return 0;
  return Math.min(1, Math.max(0, seconds / TEST_BUILD_LIFETIME_SECONDS));
}

/** Short time-left label for a chip: "5 d", "5 h", "45 m", or "now". */
export function formatTimeLeftShort(seconds: number): string {
  if (!Number.isFinite(seconds)) return "now";
  if (seconds >= 86400) return `${Math.floor(seconds / 86400)} d`;
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)} h`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)} m`;
  return "now";
}

export type TestBuildFilter = "all" | "expiring" | "mine";

export function filterTestBuilds(
  builds: TestBuild[],
  filter: TestBuildFilter,
  currentUserEmail?: string
): TestBuild[] {
  if (filter === "expiring") {
    return builds.filter((b) => isExpiringSoon(b.expires_in_seconds));
  }
  if (filter === "mine") {
    if (!currentUserEmail) return [];
    return builds.filter((b) => b.uploaded_by_email === currentUserEmail);
  }
  return builds;
}

/**
 * Filename to save a downloaded test build under. The iteration is part of the
 * name because a QE will often have three iterations of the same version in
 * their downloads folder at once.
 */
export function getTestBuildFilename(build: TestBuild): string {
  const base = [
    build.name || build.versionName,
    build.versionName,
    build.versionCode,
    `iter${build.iteration}`,
  ]
    .filter(Boolean)
    .join("-")
    .replace(/\s+/g, "_");
  return `${base}.${getFileExtension(build)}`;
}

export default groupTestBuilds;
